import {
  DestroyRef,
  DOCUMENT,
  inject,
  Injectable,
  InjectionToken,
  Signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
} from '@angular/router';
import {Store} from '@ngrx/store';
import {
  MatomoInitializerService,
  MatomoTracker,
} from 'ngx-matomo-client';
import {filter} from 'rxjs';
import {environment} from '../../../environments/environment';
import {isMatomoConfigured} from '../../functions/matomo';
import {IMatomoConfig} from '../../models/matomo-config.model';
import {selectLoggedIn} from '../../store/auth/auth.selectors';
import {AppState} from '../../store/store';
import {PreviewModeService} from '../preview-mode/preview-mode.service';

/** Matomo tracker the page views go to, from the environment */
export const MATOMO_CONFIG: InjectionToken<IMatomoConfig> = new InjectionToken<IMatomoConfig>('MATOMO_CONFIG', {
  providedIn: 'root',
  factory: (): IMatomoConfig => environment.matomo,
});

@Injectable({
  providedIn: 'root',
})
export class MatomoService {

  private readonly config: IMatomoConfig = inject(MATOMO_CONFIG);

  private readonly router: Router = inject(Router);

  private readonly document: Document = inject(DOCUMENT);

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly tracker: MatomoTracker = inject(MatomoTracker);

  private readonly matomoInitializer: MatomoInitializerService = inject(MatomoInitializerService);

  private readonly previewModeService: PreviewModeService = inject(PreviewModeService);

  private readonly isLoggedIn: Signal<boolean> = toSignal(inject(Store<AppState>).select(selectLoggedIn), {
    initialValue: false,
  });

  private isTrackerInitialized = false;

  private previousUrl: string | null = null;

  /** Tracks a page view after every navigation, where the environment names a Matomo tracker */
  public startPageViewTracking(): void {
    if (!isMatomoConfigured(this.config)) {
      return;
    }
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.trackPageView());
  }

  /** Sends the current route to Matomo for a logged-in client, never for a preview or a guest */
  private trackPageView(): void {
    if (!this.isLoggedIn() || this.previewModeService.isInPreviewMode()) {
      return;
    }
    const url = this.document.location.origin + (this.getRoutePattern(this.router.routerState.snapshot.root) || '/');
    this.initializeTracker();
    this.tracker.setReferrerUrl(this.previousUrl ?? this.document.location.origin);
    this.tracker.setCustomUrl(url);
    this.tracker.trackPageView();
    this.previousUrl = url;
  }

  /** Builds the path out of the route patterns, so the client's username never reaches Matomo */
  private getRoutePattern(route: ActivatedRouteSnapshot): string {
    const path = route.routeConfig?.path ? `/${route.routeConfig.path}` : '';
    return route.firstChild ? path + this.getRoutePattern(route.firstChild) : path;
  }

  /** Hands the tracker to the deferred Matomo setup on the first tracked page, which loads the script */
  private initializeTracker(): void {
    if (this.isTrackerInitialized) {
      return;
    }
    this.isTrackerInitialized = true;
    this.matomoInitializer.initializeTracker({trackerUrl: this.config.trackerUrl, siteId: this.config.siteId});
  }

}
